import { useState, useEffect, useRef } from 'react'

// Rendu SVG partage des annotations, extrait de AnnotationOverlay pour que le
// mode lecture puisse afficher un jeu sans embarquer l'editeur (outils,
// toolbar, poignees, raccourcis). Une seule implementation du dessin : ce qui
// est enregistre et ce qui est relu sont peints par le meme code.
//
// Les 6 types sont en coordonnees normalisees 0-1, rendues dans un viewBox
// 1600x900 avec preserveAspectRatio="none" : la couche s'etire exactement sur
// la video quel que soit son format.
//
// LISIBILITE SUR LE GAZON (ANNOTATIONS-001 lot 3). Trois regles, tirees de Once
// Video Analyser et Nacsport :
//   1. contour net et sature, d'epaisseur CONSTANTE quelle que soit la taille
//      d'affichage — voir `k` ci-dessous ;
//   2. remplissage tres leger, le contour porte la lecture ;
//   3. une ombre portee discrete, parce que le vert du terrain absorbe les
//      couleurs froides.
// Le libelle garde son traitement d'origine (contour noir en paint-order) : il
// fonctionne, et il reste donc hors du groupe qui porte l'ombre.

export const VB_W = 1600
export const VB_H = 900

export const BLUE   = '#4F8EF7'
export const RED    = '#E24B4A'
export const YELLOW = '#EF9F27'
export const VIOLET = '#9B6FF7'
export const WHITE  = '#FFFFFF'

export const COLORS = [BLUE, RED, YELLOW, VIOLET, WHITE]

export function colorId(c) { return c.replace('#', '') }

// Aplatissement de l'ellipse de tag joueur. C'est ce rapport qui donne
// l'impression d'une forme posee au sol plutot que d'un cercle autour du buste.
// Une seule definition : le halo, l'anneau de liaison et le contour de
// selection le lisent tous ici, sinon ils divergeront a l'ecran.
export const HALO_RATIO = 3
export const haloRy = (rx) => rx / HALO_RATIO

// Le tag joueur est une EMPREINTE, pas une zone. Au-dela d'une certaine taille
// son voile devient une flaque sombre et l'ellipse cesse d'etre serree autour
// des pieds — c'est le defaut que le plafond supprime, plutot que d'alleger le
// remplissage en fonction de la taille. Un remplissage qui depend du rayon
// ferait dependre le style de la geometrie : deux regles a tenir au lieu d'une,
// et la flaque resterait, juste plus pale. Pour marquer une surface, l'outil
// Zone existe.
//
// Aucun halo enregistre n'approche ce plafond · les deux de la prod sont a 18,3
// et 17,5 (mesure du 12/08/2026). Il ne reecrit donc rien.
export const HALO_MIN_RX = 12
export const HALO_MAX_RX = 110

/** Demi-grand axe reellement dessine. Une seule definition des bornes. */
export function haloRx(a) {
  return Math.min(Math.max(a?.rx || 30, HALO_MIN_RX), HALO_MAX_RX)
}

// Part de `ry` dont l'empreinte descend sous le point clique. Le point clique
// vise le joueur ; ses pieds sont un peu plus bas, et c'est la que doit tomber
// le CENTRE de l'ellipse.
export const HALO_ANCHOR_SHARE = 1 / 3

/**
 * TROMPE-L'OEIL D'OCCLUSION.
 *
 * Chez Once, l'ellipse passe DERRIERE le joueur : ses jambes coupent l'arc
 * superieur, et c'est ce qui donne l'impression d'un cercle peint sur la
 * pelouse. Notre SVG est une couche unique au-dessus de la video, donc le trait
 * passe devant tout — d'ou l'effet plat. Reproduire l'occlusion pour de vrai
 * demanderait de detecter la silhouette du joueur ; c'est un autre chantier.
 *
 * A defaut, on attenue l'arc superieur · un trait qui s'efface la ou le joueur
 * devrait le masquer se lit comme un trait masque, l'oeil complete. L'arc
 * inferieur, lui, passe reellement devant le joueur et reste a pleine opacite.
 *
 * Le plancher n'est PAS zero. Mesure sur gazon, ecart de luminance au sommet de
 * l'arc superieur · 1 a plancher 0, 24 a plancher 0,15, contre 147 pour l'arc
 * inferieur. A zero l'ellipse se lit comme COUPEE et non comme masquee, ce qui
 * casse l'illusion au lieu de la servir.
 */
export const HALO_FADE_FLOOR = 0.15

// Les deux issues de la branche `isOpp` — et elles seules. La branche continue
// de choisir laquelle ; seul l'usage de son resultat change.
const HALO_STROKE_COLORS = ['#ffffff', RED]

/**
 * Centre de l'ellipse DESSINEE, en coordonnees normalisees.
 *
 * Reference Once Sport · le joueur est au MILIEU de l'ellipse, ses pieds au
 * centre vertical. L'arc superieur passe derriere lui, l'arc inferieur devant.
 * Deux erreurs a eviter, de part et d'autre · centrer sur le point clique fait
 * passer l'arc superieur devant les jambes ; descendre d'un `ry` entier pose le
 * joueur sur le bord haut, l'ellipse entierement sous lui. On descend d'un
 * tiers de `ry` : le point clique tombe dans le tiers superieur de l'ellipse,
 * les deux arcs l'encadrent.
 *
 * C'EST UNE REGLE DE RENDU, PAS UNE DONNEE. Rien n'est reecrit en base : les
 * annotations deja enregistrees en beneficient sans migration. Ce qui suppose
 * que TOUT ce qui positionne un halo passe par ici — le dessin, mais aussi le
 * hit-test, les poignees, le contour de selection, l'anneau de liaison et les
 * sommets d'une ligne tactique. Un seul oubli et on clique a un endroit pour
 * selectionner a un autre.
 */
export function haloAnchor(a) {
  const p = a?.points?.[0]
  if (!p) return null
  return { x: p.x, y: p.y + haloRy(haloRx(a)) * HALO_ANCHOR_SHARE / VB_H }
}

const SHADOW_ID = 'ano-shadow'

/**
 * Facteur d'echelle des DECORATIONS (epaisseurs de trait, tirets, pointes de
 * fleche, hachures, ombre). Les geometries — points, `rx` du halo — n'y touchent
 * pas : ce sont des donnees, elles doivent suivre l'image.
 *
 * Sans lui, `strokeWidth` est en unites de viewBox : un trait de 4 sort a 4 px
 * sur le PNG exporte (canvas 1600), a 2,25 px sur un player de 900 et a 0,94 px
 * en portrait 375 — sous le pixel, un gris antialiase au lieu d'un contour.
 * Trois surfaces, trois epaisseurs, pour un critere qui dit « constante ».
 *
 * `k = 1600 / largeur affichee` annule exactement la transformation du viewBox.
 * Sur le PNG, le SVG est serialise tel qu'affiche puis rasterise a 1600 : le
 * trait y sort proportionnel a ce que le coach avait sous les yeux.
 */
export function useStrokeScale(ref) {
  const [k, setK] = useState(1)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const read = () => {
      const w = el.clientWidth || el.getBoundingClientRect().width
      // Une largeur nulle (couche pas encore posee) laisserait passer une
      // division par zero : on garde la valeur precedente.
      if (w > 0) setK(VB_W / w)
    }
    read()
    const ro = new ResizeObserver(read)
    ro.observe(el)
    return () => ro.disconnect()
  }, [ref])
  return k
}

// Marqueurs de fleche, hachures de zone et ombre portee. Les ids sont globaux
// au document : deux <svg> montes en meme temps partageraient les memes defs,
// ce qui est sans consequence puisqu'elles sont identiques.
//
// La pointe de fleche est en markerUnits="strokeWidth" : elle suit donc `k`
// toute seule, par l'epaisseur du trait. Les hachures, elles, sont en
// userSpaceOnUse et doivent etre mises a l'echelle explicitement — sinon le
// remplissage de zone serait la seule chose a changer de taille avec l'ecran.
export function AnnotationDefs({ k = 1 }) {
  const hatch = 14 * k
  return (
    <defs>
      {COLORS.map(c => (
        <marker key={c} id={`arrow-${colorId(c)}`} markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto" markerUnits="strokeWidth">
          <polygon points="0,0 9,4.5 0,9" fill={c} />
        </marker>
      ))}
      {COLORS.map(c => (
        <pattern key={c} id={`hatch-${colorId(c)}`} width={hatch} height={hatch} patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
          <rect width={hatch} height={hatch} fill="none" />
          <line x1="0" y1="0" x2="0" y2={hatch} stroke={c} strokeWidth={6 * k} opacity="0.55" />
        </pattern>
      ))}
      {/* filterUnits="userSpaceOnUse" et non le defaut objectBoundingBox : la
          boite d'un trait horizontal a une hauteur nulle, et une region de
          filtre nulle fait DISPARAITRE l'element. La region couvre donc tout le
          viewBox. C'est aussi pourquoi l'ombre est posee une seule fois sur le
          groupe et non sur chaque forme — une region pleine page par element
          couterait autant de tampons que de formes. */}
      <filter id={SHADOW_ID} filterUnits="userSpaceOnUse" x="0" y="0" width={VB_W} height={VB_H}>
        <feDropShadow dx="0" dy={2 * k} stdDeviation={2.5 * k} floodOpacity="0.45" />
      </filter>

      {/* Rampe d'attenuation du halo (cf. HALO_FADE_FLOOR). Verticale : `y2=1`
          n'est pas un detail, le defaut de linearGradient est HORIZONTAL et
          effacerait le cote gauche au lieu du haut.
          En unites de boite d'objet, donc une seule def par couleur sert tous
          les halos quelles que soient leur position et leur taille. Le trait
          deborde de la boite ; le degrade s'y sature proprement, ce qu'un
          masque ne sait pas faire sans que sa rampe depende de la taille. */}
      {HALO_STROKE_COLORS.map(c => (
        <linearGradient key={c} id={`halo-stroke-${colorId(c)}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={c} stopOpacity={HALO_FADE_FLOOR} />
          <stop offset="0.5" stopColor={c} stopOpacity={(HALO_FADE_FLOOR + 1) / 2} />
          <stop offset="1" stopColor={c} stopOpacity="1" />
        </linearGradient>
      ))}
      {/* Le remplissage suit la meme rampe · un voile uniforme sous un trait qui
          s'efface se remarquerait. Son intensite reste gouvernee par
          `fillOpacity` sur l'ellipse : ce degrade donne la FORME du voile, pas
          sa force. */}
      {COLORS.map(c => (
        <linearGradient key={c} id={`halo-fill-${colorId(c)}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={c} stopOpacity={HALO_FADE_FLOOR} />
          <stop offset="1" stopColor={c} stopOpacity="1" />
        </linearGradient>
      ))}
    </defs>
  )
}

/**
 * Peint un element. `opts` porte ce qui n'a de sens qu'en edition :
 *   haloById  : Map id -> halo, pour resoudre les sommets d'une ligne tactique
 *   linkingIds: halos en cours de liaison (anneau jaune pulse)
 *   onHaloDown: handler pointerdown pose sur chaque halo
 * En lecture, tout est absent : aucun curseur pointer, aucun handler.
 * `k` met les decorations a l'echelle (cf. useStrokeScale) ; a 1, le rendu est
 * celui d'avant le lot 3.
 */
export function renderElement(a, opts = {}) {
  const { haloById, linkingIds, onHaloDown, interactive = false, k = 1 } = opts
  const cursor = interactive ? { cursor: 'pointer' } : undefined

  switch (a.type) {
    case 'halo': {
      // La branche `isOpp` est celle de l'adversaire. Elle n'est PAS touchee par
      // le lot 3 (cf. P0-COULEURS-ANCRAGE-FAUX-001) : les deux variantes
      // recoivent exactement le meme traitement visuel, seuls la couleur et le
      // tirete du contour continuent d'en dependre.
      const isOpp  = a.color === YELLOW
      const stroke = isOpp ? RED : '#ffffff'
      const dash   = isOpp ? `${5 * k},${3 * k}` : undefined
      const anchor = haloAnchor(a)
      const cx = X(anchor.x), cy = Y(anchor.y)
      const rx = haloRx(a)
      const ry = haloRy(rx)
      const linking = !!linkingIds && linkingIds.includes(a.id)
      return (
        <g key={a.id} onPointerDown={onHaloDown ? onHaloDown(a) : undefined} style={cursor}>
          {linking && (
            // Meme ellipse, 8 unites plus large : le rapport d'aplatissement est
            // conserve exactement, l'anneau reste parallele au halo.
            <ellipse cx={cx} cy={cy} rx={rx + 8} ry={haloRy(rx + 8)} fill="none" stroke={YELLOW} strokeWidth={3 * k} pointerEvents="none">
              <animate attributeName="opacity" values="1;0.3;1" dur="1s" repeatCount="indefinite" />
            </ellipse>
          )}
          {/* C'est le CONTOUR qui porte la lecture, le remplissage n'est qu'un
              appui — d'ou un trait plus epais que celui des autres formes et un
              voile a 12 %. A 70 %, le halo masquait le joueur au lieu de le
              designer.
              Contour et voile passent par la rampe d'attenuation : l'arc
              superieur s'efface, l'arc inferieur reste plein. Repli sur une
              couleur plate si la teinte ne fait pas partie de la palette — une
              reference a un degrade inexistant ne peindrait rien du tout. */}
          <ellipse cx={cx} cy={cy} rx={rx} ry={ry}
            fill={COLORS.includes(a.color) ? `url(#halo-fill-${colorId(a.color)})` : a.color}
            fillOpacity={0.12}
            stroke={`url(#halo-stroke-${colorId(stroke)})`}
            strokeWidth={3.5 * k} strokeDasharray={dash}
            pointerEvents={interactive ? 'all' : 'none'} />
        </g>
      )
    }

    // Fleche : 3 styles (straight | dashed | curved). Le style courbe utilise
    // un point de controle Bezier quadratique (a.control, ou recalcule).
    case 'arrow': {
      const [p1, p2] = a.points
      const style = a.arrow_style || 'straight'
      const common = {
        stroke: a.color, strokeWidth: 4 * k, strokeLinecap: 'round',
        markerEnd: `url(#arrow-${colorId(a.color)})`,
        style: cursor,
      }
      if (style === 'curved') {
        const c = a.control || curveControl(p1, p2)
        return (
          <path key={a.id} d={`M ${X(p1.x)} ${Y(p1.y)} Q ${X(c.x)} ${Y(c.y)} ${X(p2.x)} ${Y(p2.y)}`}
            fill="none" {...common} />
        )
      }
      return (
        <line key={a.id}
          x1={X(p1.x)} y1={Y(p1.y)} x2={X(p2.x)} y2={Y(p2.y)}
          strokeDasharray={style === 'dashed' ? `${12 * k},${7 * k}` : undefined}
          {...common} />
      )
    }

    // Ligne / passe : trait pointille avec pointe (outil distinct de la fleche).
    case 'line': {
      const [p1, p2] = a.points
      return (
        <line key={a.id}
          x1={X(p1.x)} y1={Y(p1.y)} x2={X(p2.x)} y2={Y(p2.y)}
          stroke={a.color} strokeWidth={4 * k} strokeLinecap="round" strokeDasharray={`${12 * k},${7 * k}`}
          markerEnd={`url(#arrow-${colorId(a.color)})`}
          style={cursor}
        />
      )
    }

    case 'zone': {
      const pts = a.points.map(p => `${X(p.x)},${Y(p.y)}`).join(' ')
      return (
        <polygon key={a.id} points={pts}
          fill={`url(#hatch-${colorId(a.color)})`} stroke={a.color} strokeWidth={2.5 * k} opacity={0.85}
          style={cursor}
        />
      )
    }

    case 'text':
      return (
        <text key={a.id} x={X(a.points[0].x)} y={Y(a.points[0].y)}
          fontFamily="Barlow Condensed, sans-serif" fontSize={34} fontWeight={700}
          fill="#fff" textAnchor="middle" stroke="rgba(0,0,0,0.55)" strokeWidth={0.6}
          paintOrder="stroke" style={cursor}
        >
          {a.text}
        </text>
      )

    case 'tactical_line': {
      // Les sommets sont les centres des empreintes au sol, et non les points
      // cliques : sans `haloAnchor` la ligne relierait les joueurs pendant que
      // les ellipses seraient dessinees plus bas, et l'ecart grandirait avec la
      // taille des halos. En lecture, haloById est reconstruit depuis le jeu
      // affiche.
      const map = haloById || new Map()
      const pts = (a.linkedHalos || []).map(id => haloAnchor(map.get(id))).filter(Boolean)
      if (pts.length < 2) return null
      const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${X(p.x)} ${Y(p.y)}`).join(' ') + ' Z'
      return (
        <path key={a.id} d={d} fill="none" stroke={a.color} strokeWidth={3 * k} strokeLinejoin="round"
          style={cursor}
        />
      )
    }

    default:
      return null
  }
}

/**
 * Peint un jeu complet. C'est CE point d'entree que l'editeur et la lecture
 * partagent, et non plus `renderElement` appele dans deux boucles paralleles :
 * le decoupage entre ce qui porte l'ombre et ce qui n'en porte pas est une
 * decision de rendu, elle ne doit pas etre reprise par chaque appelant.
 *
 * Les formes passent par un groupe unique porteur de l'ombre. Le libelle reste
 * dehors : son contour noir en paint-order le decolle deja du fond, et le brief
 * demande de ne pas y toucher. Consequence assumee — un libelle est desormais
 * toujours peint au-dessus des formes, quel que soit son rang dans le tableau.
 */
export function renderElements(elements, opts = {}) {
  const list = elements || []
  const formes  = list.filter(a => a.type !== 'text')
  const libelles = list.filter(a => a.type === 'text')
  return (
    <>
      <g filter={`url(#${SHADOW_ID})`}>{formes.map(a => renderElement(a, opts))}</g>
      {libelles.map(a => renderElement(a, opts))}
    </>
  )
}

// Point de controle Bezier d'une fleche courbe : perpendiculaire au milieu du
// segment, offset 30% de la longueur (coordonnees normalisees).
export function curveControl(a, b) {
  const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2
  const dx = b.x - a.x, dy = b.y - a.y
  const len = Math.hypot(dx, dy) || 1
  const off = len * 0.3
  return { x: mx + (-dy / len) * off, y: my + (dx / len) * off }
}

const X = (v) => v * VB_W
const Y = (v) => v * VB_H

export function haloMapOf(elements) {
  return new Map((elements || []).filter(a => a.type === 'halo').map(a => [a.id, a]))
}

/**
 * Couche d'affichage en lecture seule : la frame annotee, sans aucun outil.
 *
 * `pointer-events: none` est structurel, pas cosmetique. En mode lecture les
 * controles du player restent actifs, et mettre la video en pause est le geste
 * qui annule le compte a rebours : la couche ne doit jamais intercepter un clic
 * destine a l'image.
 */
export default function AnnotationLayer({ elements = [], style }) {
  const svgRef = useRef(null)
  // Le hook est appele avant tout retour anticipe : un `return null` place
  // au-dessus le rendrait conditionnel, ce que React interdit.
  const k = useStrokeScale(svgRef)
  const haloById = haloMapOf(elements)
  if (!elements.length) return null
  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        zIndex: 3,
        ...style,
        // APRES le spread, deliberement : c'est une propriete de securite, pas
        // un defaut de style. Figer l'image passe par les controles du player,
        // la couche ne doit jamais intercepter un clic destine a la video —
        // et ca ne doit pas dependre de la discipline des appelants futurs.
        pointerEvents: 'none',
      }}
    >
      <AnnotationDefs k={k} />
      {renderElements(elements, { haloById, k })}
    </svg>
  )
}
